import React from 'react';
import { motion } from 'framer-motion';
import { useGentleMotion as useReducedMotion } from '../hooks/useGentleMotion';
import { useIdleReady } from '../hooks/useIdleReady';
type DoodleKind = 'star' | 'confetti' | 'crayon' | 'sparkle' | 'squiggle' | 'dot';
interface DoodleSpec {
  kind: DoodleKind;
  top: string;
  left: string;
  size: number;
  color: string;
  rotate?: number;
  delay?: number;
}
function Shape({
  kind,
  color,
  size
}: {
  kind: DoodleKind;
  color: string;
  size: number;
}) {
  const common = {
    width: size,
    height: size,
    fill: 'none' as const
  };
  switch (kind) {
    case 'star':
      return <svg {...common} viewBox="0 0 24 24" aria-hidden="true" data-id="element-111" data-mp="3b63fi0tvvcql">
          <path d="M12 2l2.6 6.4L21 10l-5 4.2 1.6 6.6L12 17.4 6.4 20.8 8 14.2 3 10l6.4-1.6L12 2z" fill={color} data-id="element-112" data-mp="36ed1nkvqm6uf" />
        </svg>;
    case 'sparkle':
      return <svg {...common} viewBox="0 0 24 24" aria-hidden="true" data-id="element-113" data-mp="2enf8ygtdem8y">
          <path d="M12 3v18M3 12h18" stroke={color} strokeWidth="2.4" strokeLinecap="round" data-id="element-114" data-mp="2emx794nxvftn" />
        </svg>;
    case 'crayon':
      return <svg {...common} viewBox="0 0 24 24" aria-hidden="true" data-id="element-115" data-mp="p2ur0qg899h5">
          <rect x="9" y="7" width="6" height="14" rx="1.6" fill={color} data-id="element-116" data-mp="twz0bgcyttle" />
          <path d="M12 2l3 5H9l3-5z" fill={color} opacity="0.65" data-id="element-117" data-mp="yvacvyunai7b" />
        </svg>;
    case 'squiggle':
      return <svg {...common} viewBox="0 0 32 16" aria-hidden="true" data-id="element-118" data-mp="18a6m2e8oqjo">
          <path d="M1 11c4-10 8 10 12 0s8 10 12 0" stroke={color} strokeWidth="2.4" strokeLinecap="round" data-id="element-119" data-mp="65jtbaa8uo3t" />
        </svg>;
    case 'confetti':
      return <svg {...common} viewBox="0 0 12 12" aria-hidden="true" data-id="element-120" data-mp="g5anfauw713d">
          <rect width="12" height="6" rx="3" fill={color} data-id="element-121" data-mp="1mklefijtkgmi" />
        </svg>;
    default:
      return <svg {...common} viewBox="0 0 12 12" aria-hidden="true" data-id="element-122" data-mp="1riwo9ta46r4q">
          <circle cx="6" cy="6" r="6" fill={color} data-id="element-123" data-mp="1rlisbixsqoi7" />
        </svg>;
  }
}

/**
 * Decorative floating doodles. Used only at strategic moments
 * (hero, bonuses, passport, final offer). Fully disabled for
 * users who prefer reduced motion.
 */
export function Doodles({
  specs
}: {
  specs: DoodleSpec[];
}) {
  const reduce = useReducedMotion();
  const idle = useIdleReady();

  // Decorative only: rendered once the browser is idle.
  if (!idle) return null;
  return <div className="pointer-events-none absolute inset-0 z-0 hidden overflow-hidden sm:block" aria-hidden="true" data-id="element-124" data-mp="34wup6zq6vbie">
      {specs.map((s, i) => <motion.div key={i} className="absolute" style={{
      top: s.top,
      left: s.left,
      rotate: `${s.rotate ?? 0}deg`
    }} animate={reduce ? undefined : {
      y: [0, -12, 0],
      rotate: [s.rotate ?? 0, (s.rotate ?? 0) + 3, s.rotate ?? 0]
    }} transition={{
      duration: 7 + i % 4,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: s.delay ?? i * 0.4
    }} data-id="element-125" data-mp="3dowqt1bzuxu9" data-mpi={i}>
          <Shape kind={s.kind} color={s.color} size={s.size} data-id="element-126" data-mp="2btse383vixil" />
        </motion.div>)}
    </div>;
}
export type { DoodleSpec };